/**
 * Typed application error with a stable error code.
 *
 * All services should throw `AppError` instead of plain `Error` so that:
 * 1. The MutationCache can map code → i18n key → user-friendly message.
 * 2. Sentry still receives the full technical `message` + original `cause`.
 * 3. Unknown errors fall back to a safe generic message automatically.
 */
export class AppError extends Error {
  constructor(
    /** Stable error code, e.g. "credits/insufficient" */
    public readonly code: string,
    /** Technical message for logs / Sentry (never shown to users) */
    message: string,
    /** Original downstream error */
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = "AppError";
  }
}

/**
 * Maps Firebase Cloud Function error codes (FunctionsError / FirebaseError)
 * to AppError so the i18n resolver can show proper user messages.
 *
 * Firebase errors have a `.code` like "functions/resource-exhausted".
 * This maps known codes to AppError codes; unknown errors pass through as-is
 * or get wrapped with code "unknown".
 */
const FIREBASE_CODE_MAP: Record<string, string> = {
  "functions/resource-exhausted": "credits/insufficient",
  "functions/unauthenticated": "auth/not_authenticated",
  "functions/permission-denied": "auth/not_authenticated",
  "functions/failed-precondition": "generation/upscale_resolution_limit",
};

export function mapFirebaseError(error: unknown): unknown {
  if (error instanceof AppError) return error;

  // Firebase errors extend Error and have a `.code` string property
  if (
    error instanceof Error &&
    "code" in error &&
    typeof (error as any).code === "string"
  ) {
    // Backend functions may attach a fine-grained AppError code via
    // HttpsError `details.appCode`. Prefer it over the generic Firebase code
    // so the resolver can show a specific translation.
    const details = (error as any).details;
    const appCodeFromDetails =
      details && typeof details === "object" && "appCode" in details
        ? (details as { appCode?: unknown }).appCode
        : undefined;
    if (
      typeof appCodeFromDetails === "string" &&
      appCodeFromDetails.length > 0
    ) {
      return new AppError(appCodeFromDetails, error.message, error);
    }

    const firebaseCode = (error as any).code as string;
    const appCode = FIREBASE_CODE_MAP[firebaseCode];
    if (appCode) {
      return new AppError(appCode, error.message, error);
    }
    // Unknown Firebase error — wrap with "unknown" code so it goes through resolver
    return new AppError("unknown", error.message, error);
  }

  return error;
}
